import type {BoxProps} from '@mantine/core';
import {Box, Group, Pagination as MantinePagination} from '@mantine/core';
import type {ReactNode} from 'react';
import React, {useEffect} from 'react';
import {Subtext} from 'src/design/atoms/Typography';

/**
 * Page control with an optional "Showing X to Y of Z" record-count caption.
 * @registry Pagination control with a "Showing X to Y of Z" caption.
 *
 * Ported from `library/tables/Pagination` (`Paginator`). Prop surface kept
 * identical except `sx`. DEVIATION: the frozen component was MUI
 * (`@mui/material` `Pagination` + `SxProps<Theme>`, both banned in
 * `design/`). Rebuilt on Mantine `Pagination` (imported as
 * `MantinePagination` so the exported `Paginator` name is unchanged).
 * `sx` is dropped — its type (`SxProps<Theme>`) comes from the banned
 * `@mui/material` import and can't be reproduced. Every current consumer of
 * the frozen `Paginator` (grepped across `src/`: `AdminTaskAssignmentsListByTask`,
 * `AdminTaskAssignmentsListByUser`, `AuditLogsList`, `NotesList`,
 * `NotificationsList`, `AttachmentsList`, `UserRelatedAttachmentsList`,
 * `EmployeeList`, `ManagerList`, `DepartmentList`, `UserGroupList`,
 * `FormSelectModalV2`) passes `sx` — but only ever for margin
 * (`mt`/`mb`) and a `sm`-breakpoint `transform: scale(0.8)`. Replaced with
 * Mantine `BoxProps` spread (`mt`/`mb`/`style` etc. as normal props) so
 * those call sites can pass the same values through named Mantine props
 * when they migrate.
 */
export interface PaginatorProps extends BoxProps {
  currentPage: number;
  totalRecords: number;
  showRecordCount?: boolean;
  perPage?: number;
  onChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  /** Extra content appended after the "Showing X to Y of Z" caption — e.g. a
   * dataset-specific summary note. Only rendered when `showRecordCount` is true. */
  captionSuffix?: ReactNode;
}
export const Paginator = ({
  onChange,
  currentPage,
  totalRecords,
  perPage = 25,
  showRecordCount = true,
  captionSuffix,
  ...boxProps
}: PaginatorProps) => {
  const lower = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const upper = Math.min((currentPage - 1) * perPage + perPage, totalRecords);
  const caption =
    totalRecords === 0
      ? 'Showing 0 of 0'
      : `Showing ${lower} to ${upper} of ${totalRecords}`;

  const totalPages = Math.ceil(totalRecords / perPage);

  // Zero records may mean "still loading", so only a known non-empty total clamps the page.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      onChange?.({} as React.ChangeEvent<unknown>, totalPages);
    }
  }, [currentPage, totalPages, onChange]);

  const handleChange = (page: number) => {
    if (onChange) {
      onChange({} as React.ChangeEvent<unknown>, page);
    }
  };

  return (
    <Box {...boxProps}>
      <MantinePagination
        value={currentPage}
        total={totalPages}
        variant="outline"
        onChange={handleChange}
      />
      {showRecordCount === true && (
        <Group gap={4} mt="sm" ml="sm" wrap="wrap">
          <Subtext text={caption} size="xs" mb={0} />
          {typeof captionSuffix === 'string' ? (
            <Subtext text={captionSuffix} size="xs" mb={0} />
          ) : (
            captionSuffix
          )}
        </Group>
      )}
    </Box>
  );
};
